import { adminApiAxios } from '@/api'
import { keyBy } from 'lodash'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  FinancialAccountReconciliationWithTransactions,
  receiveReconciliations,
  receiveSingleReconciliation,
} from '../../reducers/admin/allFinancialAccountReconciliationsReducer'
import { fetchIfNeededWrapper, fetchWrapper } from '../../reducers/fetch'
import {
  Reconciliation,
  ReconciliationOrigin,
} from '../../reducers/admin/financialAccountsReducer'
import { getReconciliationById } from '../../selectors/reconciliations.selectors'
import { dollarsToCents } from '../../utils/currencyHelpers'

export interface ReconciliationUpsertModel {
  accountId: number
  endingBalanceDate: DateTime
  endingBalanceInCents: number
  startingBalanceDate: DateTime
  startingBalanceInCents: number
  status: string
  bookkeepingReportId: number
}

export interface VirtualStatementTransaction {
  transactionId: number | null
  date: string
  description: string | null
  amountInCents: number
  balanceInCents: number | null
  deltaInCents: number | null
  potentialBreak: boolean
  displayStatus: boolean
}

export interface VirtualStatementReconciliation {
  id: number
  startingBalanceInCents: number
  endingBalanceInCents: number
  startingBalanceDate: string
  endingBalanceDate: string
  status: string
  origin: ReconciliationOrigin
  bookkeepingProcess: string
  financialAccount: {
    id: number
    plaidInstitutionName: string
    name: string
    mask: string
    type: string
    subtype: string
  }
}

type VirtualStatementReportData = {
  id: number
  date: string | null
}
export interface VirtualStatementData {
  reconciliation: VirtualStatementReconciliation
  statement: VirtualStatementTransaction[]
  statementUrl: string
  blocked: boolean
  currentReport: VirtualStatementReportData | null
  /** The head of the blocked chain: the earliest month that still needs reconciling. */
  earliestBlockingReport: VirtualStatementReportData | null
}

/** Recover can settle a period and reopen its predecessor, so it answers with both. */
export interface ReconWithAnalyticsScenarioResponse {
  reconciliations: FinancialAccountReconciliationWithTransactions[]
  analyticsScenario?: string | null
}

/** One account and one period, so `null` when the run declined to write. */
export interface AutoReconciliationResponse {
  reconciliation: FinancialAccountReconciliationWithTransactions | null
  analyticsScenario?: string | null
}
/*
  GET One
*/
export const getFetchSingleAccountRecKey = (id: number | string) =>
  `FETCH_SINGLE_ACCOUNT_REC_KEY${id}`
export const fetchSingleAccountReconciliationIfNeeded = (id: number | string) =>
  fetchIfNeededWrapper({
    fetchKey: getFetchSingleAccountRecKey(id),
    defaultErrorMessage: 'Error fetching single account reconciliation',
    shouldHandleError: false,
    defaultValueSelector: (state) => getReconciliationById(state, id),
    fetchFunction: async (dispatch) => {
      const json =
        await adminApiAxios.get<FinancialAccountReconciliationWithTransactions>(
          `/finances/api/v1/admin/reconciliations/${id}`
        )
      dispatch(receiveSingleReconciliation(json.data))
      return json.data
    },
  })

/*
  Get all for User
*/

export const fetchAllFinancialAccountReconciliationsForUser = (
  userId: number
) =>
  fetchWrapper({
    defaultErrorMessage:
      'There was an error fetching all user financial account reconciliations.',
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.get<Reconciliation[]>(
        `/finances/api/v1/admin/reconciliations/${userId}/list`
      )
      dispatch(receiveReconciliations(keyBy(json.data, 'id')))
      return json.data
    },
  })

/*
  UPSERT
*/

export const upsertAccountReconciliation = (
  id: number | undefined,
  data: ReconciliationUpsertModel
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error upserting account reconciliation',
    shouldHandleError: false,
    fetchFunction: async (dispatch) => {
      const json =
        await adminApiAxios.post<FinancialAccountReconciliationWithTransactions>(
          `/finances/api/v1/admin/reconciliations/${id ? id : ''}`,
          {
            ...data,
            startingBalanceDate: data.startingBalanceDate.toFormat(
              DATE_FORMATS_LUXON.YEAR_MONTH_DAY
            ),
            endingBalanceDate: data.endingBalanceDate.toFormat(
              DATE_FORMATS_LUXON.YEAR_MONTH_DAY
            ),
          }
        )
      dispatch(receiveSingleReconciliation(json.data))

      return json.data
    },
  })

export const performAutoReconciliation = (
  accountId: number,
  bookkeepingReportId: number
) =>
  fetchWrapper({
    shouldHandleError: false,
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.post<AutoReconciliationResponse>(
        `/finances/api/v1/admin/accounts/${accountId}/auto_reconciliation`,
        { bookkeepingReportId }
      )
      const { reconciliation } = json.data
      if (!reconciliation) {
        throw new Error('Error performing auto reconciliation')
      }
      dispatch(receiveSingleReconciliation(reconciliation))
      return {
        reconciliation,
        analyticsScenario: json.data.analyticsScenario,
      }
    },
  })

export const getVirtualStatement = (reconciliationId: number) =>
  fetchWrapper({
    shouldHandleError: false,
    fetchFunction: async () => {
      const json = await adminApiAxios.get<VirtualStatementData>(
        `/finances/api/v1/admin/reconciliations/${reconciliationId}/virtual_statement`
      )
      return json.data
    },
  })

export const RECOVER_RECONCILIATION_KEY = 'RECOVER_RECONCILIATION_KEY'
export const recoverAutoReconciliation = (
  reconciliationId: number,
  endDate: DateTime,
  endAmount: string
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error recovering reconciliation',
    fetchKey: RECOVER_RECONCILIATION_KEY,
    fetchFunction: async (dispatch) => {
      const endDateISO = endDate.toISODate()
      const endAmountInCents = dollarsToCents(Number(endAmount))
      const json = await adminApiAxios.post<ReconWithAnalyticsScenarioResponse>(
        `/finances/api/v1/admin/reconciliations/${reconciliationId}/recover`,
        {
          endDate: endDateISO,
          endAmountInCents,
        }
      )
      dispatch(receiveReconciliations(keyBy(json.data.reconciliations, 'id')))

      // Recover can update multiple reconciliations, but we only want to return the one we're recovering
      const reconciliation = json.data.reconciliations.find(
        (recon) => recon.id === reconciliationId
      )
      if (!reconciliation) {
        throw new Error('Error recovering reconciliation')
      }

      return {
        reconciliation,
        analyticsScenario: json.data.analyticsScenario,
      }
    },
  })

export const REOPEN_RECONCILIATION_KEY = 'REOPEN_RECONCILIATION_KEY'
export const reopenReconciliation = (reconciliationId: number) =>
  fetchWrapper({
    defaultErrorMessage: 'Error reopening reconciliation',
    fetchKey: REOPEN_RECONCILIATION_KEY,
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.post<Reconciliation>(
        `/finances/api/v1/admin/reconciliations/${reconciliationId}/reopen`
      )
      // Reopening releases every transaction link, so an empty set is the row's real
      // state rather than a gap in the response.
      dispatch(receiveSingleReconciliation({ ...json.data, transactions: [] }))
      return json.data
    },
  })

export enum CUBKAutoReconStatus {
  Success = 'success',
  Failed = 'failed',
  CascadeFailed = 'cascade_failed',
  ManualReconFailed = 'manual_recon_failed',
}

export type BookkeepingProcess = 'monthEnd' | 'catchUp'

/** The writer's decision for a manual-reconciliation write. */
export type ReconciliationWriteOutcome =
  | 'create'
  | 'updateIncomplete'
  | 'updateComplete'
  | 'reuseSettled'
  | 'declineProcessMismatch'
  | 'declineBooksComplete'

export interface PerformManualReconciliationResult {
  reconciliation: FinancialAccountReconciliationWithTransactions
  outcome: ReconciliationWriteOutcome
}

export const PERFORM_MANUAL_RECONCILIATION_KEY =
  'PERFORM_MANUAL_RECONCILIATION_KEY'

/**
 * Runs the admin manual reconciliation through the backend recon engine, returning
 * the surviving reconciliation and the writer `outcome`. Branch on `outcome`
 * (`reuseSettled` = the write did not apply) rather than inferring from the row.
 */
export const performManualReconciliation = (
  financialAccountId: number,
  data: {
    bookkeepingReportId: number
    startingBalanceDate: string
    startingBalanceInCents: number
    endingBalanceDate: string
    endingBalanceInCents: number
    bookkeepingProcess: BookkeepingProcess
  }
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error performing manual reconciliation',
    fetchKey: PERFORM_MANUAL_RECONCILIATION_KEY,
    shouldHandleError: false,
    fetchFunction: async (
      dispatch
    ): Promise<PerformManualReconciliationResult> => {
      const json = await adminApiAxios.post<
        FinancialAccountReconciliationWithTransactions & {
          writeOutcome: ReconciliationWriteOutcome
        }
      >(
        `/finances/api/v1/admin/accounts/${financialAccountId}/manual_reconciliation`,
        data
      )
      const reconciliation = json.data
      dispatch(receiveSingleReconciliation(reconciliation))

      return { reconciliation, outcome: reconciliation.writeOutcome }
    },
  })

export const AUTO_RECON_CUBK_KEY = 'AUTO_RECON_CUBK_KEY'
export const autoReconCUBK = (
  financialAccountId: number,
  data: {
    bookkeepingReportId: number
    startingBalanceDate: string
    startingBalanceInCents: number
    endingBalanceDate: string
    endingBalanceInCents: number
  }
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error fetching filtered transactions',
    fetchKey: AUTO_RECON_CUBK_KEY,
    shouldHandleError: false,
    fetchFunction: async () => {
      const json = await adminApiAxios.post<{
        status: CUBKAutoReconStatus
        manualReconciliation: FinancialAccountReconciliationWithTransactions
        reconciliation?: FinancialAccountReconciliationWithTransactions
        reason?: string
        message?: string
      }>(
        `/finances/api/v1/admin/accounts/${financialAccountId}/auto_recon_cubk`,
        {
          ...data,
          bookkeepingProcess: 'catchUp',
        }
      )
      return json.data
    },
  })

export const RETRY_AUTO_RECON_CUBK_KEY = 'RETRY_AUTO_RECON_CUBK_KEY'
export const retryAutoReconCUBK = (financialAccountId: number) =>
  fetchWrapper({
    defaultErrorMessage: 'Error retrying CUBK auto-reconciliation',
    fetchKey: RETRY_AUTO_RECON_CUBK_KEY,
    shouldHandleError: false,
    fetchFunction: async () => {
      const json = await adminApiAxios.post<{
        status: CUBKAutoReconStatus
        reason?: string
        message?: string
      }>(
        `/finances/api/v1/admin/accounts/${financialAccountId}/auto_recon_cubk/retry`,
        {}
      )
      return json.data
    },
  })
